import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { InvoiceStatus } from '../../common/constants';

type Props = {
  open?: boolean;
  editedEntityName?: string;
  invoiceStatus?: InvoiceStatus | null;
  title?: string;
  onApprove: (comment?: string) => void;
  onReject: (comment?: string) => void;
  onComment: (comment?: string) => void;
  onCancel: () => void;
};

const ApproveRejectModal = ({
  open = false,
  editedEntityName = '',
  invoiceStatus = null,
  title = '',
  onApprove,
  onReject,
  onComment,
  onCancel
}: Props) => {
  const [comment, setComment] = useState<string>('');

  const handleApprove = () => {
    setComment('');
    onApprove(comment);
  }

  const handleComment = () => {
    setComment('');
    onComment(comment);
  }

  const handleReject = () => {
    setComment('');
    onReject(comment);
  }

  const handleCancel = () => {
    setComment('');
    onCancel();
  }

  switch (invoiceStatus) {
    case InvoiceStatus.Approved:
      title = 'Proofing Approval';
      break;
    case InvoiceStatus.Rejected:
      title = 'Proofing Rejection';
      break;
    case InvoiceStatus.Pending:
      title = 'Proofing Comment';
      break;
    default:
      break;
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as='div'
        className='fixed z-10 inset-0 overflow-y-auto'
        // Passing empty function keeps dialog open when user clicks outside it.
        // Close function is handled exclusively by "cancel" button.
        onClose={() => { }}
      >
        <div className='flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className='hidden sm:inline-block sm:align-middle sm:h-screen'
            aria-hidden='true'
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            enterTo='opacity-100 translate-y-0 sm:scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
          >
            <div className='inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full'>
              {
                <Fragment>
                  <div className='m-5'>
                    <h1 className='my-5'>{title}</h1>
                    <h2 className='font-bold my-5'>{editedEntityName}</h2>
                    <p>Feedback (required if rejected or comment only)</p>
                    <div className="flex justify-center">
                      <textarea
                        className='w-90'
                        id='comment-textarea'
                        maxLength={500}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}></textarea>
                    </div>
                  </div>
                  <div className='bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse'>
                    {
                      invoiceStatus == InvoiceStatus.Approved &&
                      <button
                        type='button'
                        disabled={false}
                        className='w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-ptsgreen-normal text-base text-white hover:bg-ptsgreen-dark focus:outline-none sm:ml-3 sm:w-auto sm:text-sm  transition ease-in-out duration-150'
                        onClick={handleApprove}
                      >
                        Approve
                      </button>
                    }
                    {
                      invoiceStatus == InvoiceStatus.Pending &&
                      <button
                        type='button'
                        disabled={!comment.length}
                        className='secondary sm:ml-3 sm:w-auto sm:text-sm'
                        onClick={handleComment}
                      >
                        Comment Only
                      </button>
                    }
                    {
                      invoiceStatus == InvoiceStatus.Rejected &&
                      <button
                        type='button'
                        disabled={!comment.length}
                        className='w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-500 text-base text-white hover:bg-red-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm  transition ease-in-out duration-150'
                        onClick={handleReject}
                      >
                        Reject
                      </button>
                    }
                    <button
                      type='button'
                      className='mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base text-gray-700 hover:bg-gray-100 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm  transition ease-in-out duration-150'
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                  </div>
                </Fragment>
              }
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ApproveRejectModal;
